import { Box, TextField, Typography } from '@mui/material';
import { useId, useState } from 'react';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';
import { ModalContainer } from 'src/components/ModalContainer';
import { type CgCustomDomain } from 'src/graphql/entities/cg-custom-domain/cg-custom-domain.types';
import { useCgCustomDomains } from 'src/graphql/entities/cg-custom-domain/use-cg-custom-domain';
import { useManageCgCustomDomain } from 'src/graphql/entities/cg-custom-domain/use-manage-cg-custom-domain';
import { usePartnerDomains } from 'src/graphql/entities/partner-domain';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks/use-notifications';
import { FALLBACK_EXAMPLE_APEX } from './cg-custom-domain-display';
import { CgCustomDomainSegments } from './cg-custom-domain-segments';
import { getGraphQLErrorMessage } from './get-graphql-error-message';
import {
  getEmailDomainWarning,
  parseCustomDomainHostname,
  suggestHostname,
} from './parse-custom-domain-hostname';
import { mutedSx } from './styles';
import { useSegmentOptions } from './use-segment-options';

const DEFAULT_HELP =
  'The address clients will see. Use a subdomain, not the root of your domain.';

export const AddCgCustomDomainModal = () => {
  const { closeModal } = useModalMethods(Modals.AddCgCustomDomain);
  const partnerId = useGlobalStore.use.selectedPartnerId();
  const { addDomain, setSegments } = useManageCgCustomDomain(partnerId);
  const { partnerDomains } = usePartnerDomains(partnerId);
  const { domains } = useCgCustomDomains(partnerId);
  const {
    segmentOptionsFor,
    unassignedBrandIds,
    isLoading: segmentsLoading,
  } = useSegmentOptions(partnerId);
  const { showSuccessAlert, showErrorAlert } = useNotifications();

  const domainId = useId();
  // Null until the user types, so the suggestion fills in once the partner's domains load.
  const [typedValue, setTypedValue] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  // Null until the user changes them, so every segment not on a domain yet starts selected.
  const [chosenBrandIds, setChosenBrandIds] = useState<string[] | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const emailDomains = partnerDomains.map(({ domain }) => domain);
  const exampleApex = emailDomains[0] ?? FALLBACK_EXAMPLE_APEX;
  // The first partner domain whose address isn't registered yet; empty once every one is.
  const registered = new Set(domains.map(({ hostname }) => hostname));
  const suggestion =
    emailDomains.map(suggestHostname).find((hostname) => !registered.has(hostname)) ?? '';
  const value = typedValue ?? suggestion;
  const parsed = parseCustomDomainHostname(value, exampleApex);
  const error = serverError ?? parsed.error;
  // Held until the segments load, so the domain isn't added without the ones it would start with.
  const canAdd = Boolean(parsed.apex) && !error && !segmentsLoading;
  const segmentOptions = segmentOptionsFor();
  const brandIds = chosenBrandIds ?? unassignedBrandIds;

  const handleAdd = async () => {
    setIsSaving(true);

    let added: CgCustomDomain | null;

    try {
      added = await addDomain(parsed.hostname);
    } catch (addError) {
      setServerError(getGraphQLErrorMessage(addError, 'Couldn’t add the domain. Try again.'));
      setIsSaving(false);
      return;
    }

    try {
      if (added && brandIds.length) await setSegments(added, brandIds);

      showSuccessAlert(`${parsed.hostname} added. Next, add its DNS record.`);
    } catch {
      // The domain is added either way, so the dialog closes and the card takes the segments.
      showErrorAlert(
        `${parsed.hostname} added, but its client segments weren’t saved. Choose them on the domain.`,
      );
    }

    closeModal();
  };

  return (
    <ModalContainer
      open
      size="small"
      title="Add custom domain"
      actions={[
        { label: 'Cancel', kind: 'secondary' },
        {
          label: 'Add domain',
          kind: 'primary',
          onClick: handleAdd,
          disabled: !canAdd,
          loading: isSaving,
          loadingLabel: 'Adding',
        },
      ]}
      onClose={closeModal}
    >
      <Typography variant="body2" sx={{ ...mutedSx, mb: 2.5 }}>
        Register the address and add its DNS record. Once it’s live, the client segments you
        choose move to it: their membership pages, client sign-in, and the links we send their
        clients.
      </Typography>

      <FormFieldLabel htmlFor={domainId}>Domain</FormFieldLabel>
      <TextField
        id={domainId}
        autoFocus
        fullWidth
        placeholder={suggestHostname(exampleApex)}
        value={value}
        onChange={(event) => {
          setTypedValue(event.target.value);
          setServerError(null);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && canAdd && !isSaving) void handleAdd();
        }}
        error={Boolean(error)}
        helperText={error ?? getEmailDomainWarning(parsed.apex, emailDomains) ?? DEFAULT_HELP}
      />

      {!segmentsLoading && segmentOptions.length ? (
        <Box sx={{ mt: 2.5 }}>
          <CgCustomDomainSegments
            brandIds={brandIds}
            options={segmentOptions}
            onChange={setChosenBrandIds}
          />
        </Box>
      ) : null}
    </ModalContainer>
  );
};
